import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-regular-svg-icons';

import { ShadowPopupWrapper } from '@client/components/common/Modal';
import { CircularProgress } from '@mui/material';
import framePng from '@design-system/components/mobile-preview/assets/frame.png';

export type PreviewType =
  | 'mobile-otp'
  | 'desktop-otp'
  | 'mobile-offer'
  | 'desktop-offer';

const CloseModalIcon = ({ onClose, position = 'right' }) => {
  const posStyles = position === 'right' ? 'right-6 top-6' : 'left-6 top-6';

  return (
    <FontAwesomeIcon
      icon={faClose}
      className={`leading-none text-sm w-[24px] h-[24px] absolute ${posStyles} cursor-pointer`}
      onClick={onClose}
    />
  );
};

export const PreviewModal = ({
  data,
  onClose,
  previewType,
  previewUrl,
}: {
  data: any;
  onClose: () => void;
  previewType: PreviewType;
  previewUrl: string;
}) => {
  const formRef = useRef<HTMLFormElement>(null!);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  useEffect(() => {
    formRef.current.submit();
  }, [data]);

  const isMobilePreview = useMemo(
    () => previewType.includes('mobile'),
    [previewType],
  );

  useEffect(() => {
    if (!isMobilePreview) return;
    setIsFrameLoaded(false);

    const handleClick = (event: MouseEvent) => {
      if (
        previewRef.current &&
        !previewRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isMobilePreview, onClose]);

  const previewWidth = previewType.includes('desktop')
    ? 'w-[90vw]'
    : 'w-[375px]';

  const closeIconPosition = previewType.includes('desktop') ? 'right' : 'left';

  return (
    <>
      <form
        ref={formRef}
        action={previewUrl}
        method="post"
        target="preview_frame"
        className="hidden"
      >
        <input name="pageConfig" value={JSON.stringify(data)} readOnly />
        <input
          type="checkbox"
          name="isMobile"
          checked={
            previewType === 'mobile-otp' || previewType === 'mobile-offer'
          }
          hidden
        />
      </form>
      <ShadowPopupWrapper {...{ onClose }}>
        {isMobilePreview ? (
          <div className="flex min-h-screen w-full items-start justify-center px-4 pb-10 pt-[15vh]">
            <div ref={previewRef} className="relative">
              <CloseModalIcon onClose={onClose} position="right" />
              <div className="relative h-[764px] w-[374px]">
                <img
                  src={framePng}
                  alt=""
                  className="absolute inset-0 h-full w-full pointer-events-none select-none"
                />
                <div className="absolute left-[18px] right-[18px] top-[15px] bottom-[15px] overflow-hidden rounded-[46px] bg-white shadow-lg">
                  {!isFrameLoaded && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                      <CircularProgress color="info" size={48} thickness={4} />
                    </div>
                  )}
                  <iframe
                    className="h-full w-full"
                    name="preview_frame"
                    onLoad={() => setIsFrameLoaded(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${previewWidth} h-[90vh] bg-white shadow-lg`}
          >
            <CloseModalIcon onClose={onClose} position={closeIconPosition} />
            <iframe className={`w-full h-full`} name="preview_frame" />
          </div>
        )}
      </ShadowPopupWrapper>
    </>
  );
};
